import sortBy from "lodash/sortBy";
import {useMemo, useState} from "react";
import {useFormContext} from "react-hook-form";

import {TagSelect} from "@/components/TagSelect/TagSelect.tsx";
import {getFormError} from "@/lib/form/form.utils.ts";
import {ServiceTag} from "@/lib/types/checks.ts";
import {ANALYZER_MAX_TAGS_PER_CHECK} from "@/pages/analyzer/Analyzer.constants.ts";
import {
  AnalyzerCreateFormValues,
  AnalyzerSettingsLock,
  AnalyzerSettingsTagsFieldProps,
  AnalyzerTagValue,
} from "@/pages/analyzer/Analyzer.types.ts";
import {buildTagsLockedExample, hasDivergentOverrides} from "@/pages/analyzer/Analyzer.utils.ts";
import {AnalyzerSettingsField} from "@/pages/analyzer/components/chat/AnalyzerSettingsField.tsx";
import {AnalyzerTagsMenuList, CreateTagContext} from "@/pages/analyzer/components/chat/AnalyzerTagsMenuList.tsx";
import {useAnalyzerContext} from "@/pages/analyzer/hooks/AnalyzerContext.tsx";
import {EditTagModal} from "@/pages/tags/edit/EditTagModal.tsx";

export const AnalyzerSettingsTagsField = ({disabled}: AnalyzerSettingsTagsFieldProps) => {
  const {formData, refetchFormData, proposedChecks, trackPreCreatedTag} = useAnalyzerContext();
  const {watch, setValue, formState} = useFormContext<AnalyzerCreateFormValues>();
  const tags = watch("tags");
  const tagNames = tags.map((tag) => tag.tag);
  const [draft, setDraft] = useState<AnalyzerTagValue[] | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleTagCreated = ({id, tag, color_hex}: ServiceTag) => {
    trackPreCreatedTag();
    setDraft((current) => {
      const base = current ?? tags;
      const kept = base.filter((entry) => entry.tag.toLowerCase() !== tag.toLowerCase());
      return [...kept, {id, tag, color_hex}];
    });
    refetchFormData();
  };

  const catalog = useMemo(() => sortBy(formData?.tags ?? [], (tag) => tag.tag), [formData]);

  const locked: AnalyzerSettingsLock | undefined = hasDivergentOverrides(proposedChecks, tagNames, "tags")
    ? {
        title: "Tags now vary across Checks.",
        example: buildTagsLockedExample(
          catalog.map((tag) => tag.tag),
          tagNames,
          proposedChecks
        ),
      }
    : undefined;

  const draftTags = draft ?? tags;
  const selectedNames = new Set(draftTags.map((tag) => tag.tag));
  const tagOptions = catalog.filter((tag) => !selectedNames.has(tag.tag));

  const selectedTags = draftTags.map((tag) => ({
    id: tag.id ?? 0,
    tag: tag.tag,
    color: 0,
    color_hex: tag.color_hex,
    usage_count: 0,
  }));

  const commitDraft = () => {
    if (draft) {
      setValue("tags", draft, {shouldDirty: true, shouldValidate: true});
    }
    setDraft(null);
  };

  return (
    <>
      <AnalyzerSettingsField
        label="Tags"
        subText="Tags applied to all proposed Checks."
        chips={tags.map((tag) => ({name: tag.tag, colorHex: tag.color_hex}))}
        onRemove={(name) => {
          setValue(
            "tags",
            tags.filter((tag) => tag.tag !== name),
            {shouldDirty: true, shouldValidate: true}
          );
        }}
        editing={draft !== null}
        onEdit={() => setDraft(tags)}
        onCancel={() => setDraft(null)}
        onSave={commitDraft}
        locked={locked}
        disabled={disabled}
        error={getFormError("tags", formState.errors)}
      >
        <CreateTagContext.Provider value={() => setShowCreateModal(true)}>
          <TagSelect
            isMulti
            autoFocus
            openMenuOnFocus
            maxSelections={ANALYZER_MAX_TAGS_PER_CHECK}
            options={tagOptions}
            value={selectedTags}
            menuPlacement="top"
            maxMenuHeight={28 * 3}
            components={{MenuList: AnalyzerTagsMenuList}}
            onChange={(value) =>
              setDraft(value.map((tag) => ({id: tag.id || undefined, tag: tag.tag, color_hex: tag.color_hex})))
            }
          />
        </CreateTagContext.Provider>
      </AnalyzerSettingsField>
      <EditTagModal
        show={showCreateModal}
        hide={() => setShowCreateModal(false)}
        onSaved={handleTagCreated}
        submitBtnText="Create + Add"
      />
    </>
  );
};
